import axios from 'axios';
import useAuth from './useAuth';

export default function useAPI() {
  const { token, setToken, client, tokenExpiration } = useAuth();
  const apiURL = `${import.meta.env.VITE_WALLETTWO_URL}`;
  const rwaURL = `${import.meta.env.VITE_RWA_API_URL}`;
  const blockchainURL = `${apiURL}/blockchain`;
  const languageUrl = `${apiURL}/language`;
  const authUrl = `${apiURL}/auth`;
  const eventUrl = `${apiURL}/event`;
  const multimediaUrl = `${apiURL}/multimedia`;
  const integrationUrl = `${apiURL}/integrations`;
  const workflowUrl = `${apiURL}/workflow`;

  const headers = async () => {
    let tokenToUse = token;
    const tokenExp = tokenExpiration();

    if (!tokenToUse || (tokenExp && tokenExp < Date.now())) {
      const { data: { token: dataToken } } = await client.token()
      tokenToUse = dataToken;
      setToken(dataToken);
      
    }

    return {
        Authorization: `Bearer ${tokenToUse}`,
    };
  };

  return {
    // Wallet shard — check if current user has a wallet configured
    getWalletShard: async () => {
      const response = await axios.get(`${authUrl}/v1/wallet/shard`, { headers: await headers() });
      return response.data;
    },

    // Networks API
    getAdminNetworks: async () => {
      const response = await axios.get(`${blockchainURL}/v1/admin/network`, { headers: await headers() });
      return response.data.networks;
    },

    getNetworks: async () => {
      const response = await axios.get(`${blockchainURL}/v2/wallet/networks`, { headers: await headers() });
      return response.data.networks;
    },

    createAdminNetwork: async (networkData) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/network`, networkData, { headers: await headers() });
      return response.data;
    },

    updateAdminNetwork: async (networkId, networkData) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/network/${networkId}`, networkData, { headers: await headers() });
      return response.data;
    },

    deleteAdminNetwork: async (networkId) => {
      const response = await axios.delete(`${blockchainURL}/v1/admin/network/${networkId}`, { headers: await headers() });
      return response.data;
    },

    // Smart Contracts API
    getAdminSmartContracts: async () => {
      const response = await axios.get(`${blockchainURL}/v1/admin/smart-contract`, { headers: await headers() });
      return response.data.smartContracts;
    },

    createAdminSmartContract: async (contractData) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/smart-contract`, contractData, { headers: await headers() });
      return response.data;
    },

    updateAdminSmartContract: async (contractId, contractData) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/smart-contract/${contractId}`, contractData, { headers: await headers() });
      return response.data;
    },

    deleteAdminSmartContract: async (contractId) => {
      const response = await axios.delete(`${blockchainURL}/v1/admin/smart-contract/${contractId}`, { headers: await headers() });
      return response.data;
    },

    getCompanySmartContracts: async (companyId) => {
      const response = await axios.get(`${blockchainURL}/v1/company/${companyId}/smart-contracts`, { headers: await headers() });
      return response.data.smartContracts;
    },

    addSmartContractToCompany: async (companyId, contractId, order) => {
      const response = await axios.post(`${blockchainURL}/v1/company/${companyId}/smart-contracts`, { contractId, order }, { headers: await headers() });
      return response.data;
    },

    updateCompanySmartContract: async (companyId, contractId, order) => {
      const response = await axios.put(`${blockchainURL}/v1/company/${companyId}/smart-contracts`, { contractId, order }, { headers: await headers() });
      return response.data;
    },

    removeSmartContractFromCompany: async (companyId, contractId) => {
      const response = await axios.delete(`${blockchainURL}/v1/company/${companyId}/smart-contracts`,{headers: await headers(), data: { contractId },});
      return response.data;
    },

    getSmartContractsByCompany: async (companyId) => {
      const response = await axios.get(`${blockchainURL}/v1/smart-contracts?companyId=${companyId}`, { headers: await headers() });
      return response.data.contracts;
    },

    // Translation Language API
    getAllLanguages: async (projectId) => {
      const response = await axios.get(`${languageUrl}/v1/${projectId}/language`, { headers: await headers() });
      return response.data.languages;
    },

    createOrUpdateLanguage: async (projectId, languageData) => {
      const response = await axios.post(`${languageUrl}/v1/${projectId}/language`, languageData, { headers: await headers() });
      return response.data;
    },

    deleteLanguage: async (projectId, languageCode) => {
      const response = await axios.delete(`${languageUrl}/v1/${projectId}/language/${languageCode}`, { headers: await headers() });
      return response.data;
    },

    checkLanguageExist: async (projectId, languageCode) => {
      const response = await axios.get(`${languageUrl}/v1/${projectId}/language/${languageCode}`, { headers: await headers() });
      return response.data.exists;
    },

    // Translation Cache API
    clearTranslationCache: async (projectId) => {
      const response = await axios.get(`${languageUrl}/v1/${projectId}/refresh`, { headers: await headers() });
      return response.data;
    },

    // Translation terms API
    getAllTerms: async (projectId) => {
      const response = await axios.get(`${languageUrl}/v1/${projectId}/term`, { headers: await headers() });
      return response.data.terms;
    },

    createOrUpdateTerm: async (projectId, termData) => {
      const response = await axios.post(`${languageUrl}/v1/${projectId}/term`, termData, { headers: await headers() });
      return response.data;
    },

    deleteTerm: async (projectId, termCode) => {
      const response = await axios.delete(`${languageUrl}/v1/${projectId}/term/${termCode}`, { headers: await headers() });
      return response.data;
    },

    checkTermExist: async (projectId, termCode) => {
      const response = await axios.get(`${languageUrl}/v1/${projectId}/term/${termCode}`, { headers: await headers() });
      return response.data.exists;
    },

    // Translation API
    getTranslation: async (projectId, languageCode) => {
      const response = await axios.get(`${languageUrl}/v1/${projectId}/translation?languageCode=${languageCode}`, { headers: await headers() });
      return response.data.translations ?? response.data;
    },

    createOrUpdateTranslation: async (projectId, translationData) => {
      const response = await axios.post(`${languageUrl}/v1/${projectId}/translation`, translationData, { headers: await headers() });
      return response.data;
    },

    deleteTranslation: async (projectId, termId, languageId) => {
      const response = await axios.delete(`${languageUrl}/v1/${projectId}/translation?termId=${termId}&languageId=${languageId}`, { headers: await headers() });
      return response.data;
    },

    translateTerm: async (projectId, termId, languageId) => {
      const response = await axios.post(`${languageUrl}/v1/${projectId}/translation/translate`, { termId, languageId }, { headers: await headers() });
      return response.data;
    },

    // Companies API
    getAdminCompanies: async () => {
      const response = await axios.get(`${authUrl}/v1/admin/company`, { headers: await headers() });
      return response.data.companies;
    },

    getCompanyDetails: async (companyId) => {
      const response = await axios.get(`${authUrl}/v1/admin/company/${companyId}`, { headers: await headers() });
      return response.data;
    },

    adminCompanyAddUser: async (companyId, email) => {
      const response = await axios.post(`${authUrl}/v1/admin/company/${companyId}/member`, { email, role: "member" }, { headers: await headers() });
      return response.data;
    },

    adminCompanyRemoveUser: async (companyId, memberIdOrEmail) => {
      const response = await axios.delete(`${authUrl}/v1/admin/company/${companyId}/member`, { headers: await headers(), data: { memberIdOrEmail } });
      return response.data;
    },

    deleteAdminCompanies: async (companyId) => {
      const response = await axios.delete(`${authUrl}/v1/admin/company/${companyId}`, { headers: await headers() });
      return response.data;
    },

    getCompanyUsers: async (companyId) => {
      const response = await axios.get(`${authUrl}/v1/${companyId}/members`, { headers: await headers() });
      return response.data.users || response.data.members || [];
    },

    enableCompanyModule: async (companyId, name) => {
      const response = await axios.post(`${authUrl}/v1/admin/company/${companyId}/module`, { name }, { headers: await headers() });
      return response.data;
    },

    disableCompanyModule: async (companyId, name) => {
      const response = await axios.delete(`${authUrl}/v1/admin/company/${companyId}/module`, { headers: await headers(), data: { name } });
      return response.data;
    },

    // Event API
    getListEvents: async (companyId) => {
      const response = await axios.get(`${eventUrl}/v1/${companyId}`, { headers: await headers() });
      return response.data?.events || [];
    },

    createEvent: async (companyId, eventData) => {
      const response = await axios.post(`${eventUrl}/v1/${companyId}`, eventData, { headers: await headers() });
      return response.data;
    },

    getEventCompanyId: async (companyId, eventId) => {
      const response = await axios.get(`${eventUrl}/v1/${companyId}/${eventId}`, { headers: await headers() });
      return response.data;
    },

    getListAttendees: async (companyId, eventId) => {
      const response = await axios.get(`${eventUrl}/v1/${companyId}/${eventId}/atendees`, { headers: await headers() });
      return response.data?.atendees || [];
    },

    updateEvent: async (companyId, eventId, eventData) => {
      const response = await axios.post(`${eventUrl}/v1/${companyId}/${eventId}`, eventData, { headers: await headers() });
      return response.data;
    },

    deleteEvent: async (companyId, eventId) => {
      const response = await axios.delete(`${eventUrl}/v1/${companyId}/${eventId}`, { headers: await headers() });
      return response.data;
    },

    inviteAttendees: async (companyId, eventId, attendeeId) => {
      const response = await axios.post(
        `${eventUrl}/v1/${companyId}/${eventId}/invite`,
        { attendeeId, eventId },
        { headers: await headers() }
      );
      return response.data;
    },

    ///Multimedia API
    uploadMultimedia: async (companyId, file) => {
      const formData = new FormData();
      formData.append("files", file);

      const authHeaders = await headers();
      delete authHeaders["Content-Type"];

      const response = await axios.post(`${multimediaUrl}/v1/${companyId}`, formData, {
        headers: authHeaders,
      });
      return response.data;
    },

    // RWAs API
    getAllRWAs: async (companyId) => {
      const response = await axios.get(`${rwaURL}/v1/company/${companyId}`, { headers: await headers() });
      return response.data?.rwas || [];
    },
    
    createRWA: async (companyId, rwaData) => {
      const response = await axios.post(`${rwaURL}/v1/company/${companyId}`, rwaData, { headers: await headers() });
      return response.data;
    },

    deleteRWA: async (companyId, rwaId) => {
      const response = await axios.delete(`${rwaURL}/v1/company/${companyId}/${rwaId}`, { headers: await headers() });
      return response.data;
    },

    updateRwa: async (companyId, rwaId, rwaData) => {
      const response = await axios.post(`${rwaURL}/v1/company/${companyId}/${rwaId}`, rwaData, { headers: await headers() });
      return response.data;
    },

    getRwaDetails: async (companyId, rwaId) => {
      const response = await axios.get(`${rwaURL}/v1/company/${companyId}/${rwaId}`, { headers: await headers() });
      return response.data;
    },

    deleteRwaDataPage: async (companyId, rwaId, rwaDataId) => {
      const response = await axios.delete(`${rwaURL}/v1/company/${companyId}/${rwaId}/data/page/${rwaDataId}`, { headers: await headers() });
      return response.data;
    },

    upsertRwaDataPage: async (companyId, rwaId, rwaData) => {
      const response = await axios.post(`${rwaURL}/v1/company/${companyId}/${rwaId}/data/page/`, rwaData, { headers: await headers() });
      return response.data;
    },

    deleteRwaDataField: async (companyId, rwaId, key) => {
      const response = await axios.delete(`${rwaURL}/v1/company/${companyId}/${rwaId}/data/${key}`, { headers: await headers() });
      return response.data;
    },

    upsertRwaDataField: async (companyId, rwaId, value) => {
      const response = await axios.post(`${rwaURL}/v1/company/${companyId}/${rwaId}/data`, { value }, { headers: await headers() });
      return response.data;
    },

    createCompanyToken: async (companyId, tokenData) => {
      const response = await axios.post(`${blockchainURL}/v2/company/${companyId}/smart-contracts/custom`, tokenData, { headers: await headers() });
      return response.data;
    },

    generateCompanySmartContract: async (companyId, { prompt, name, contractType, sourceCode }) => {
      const body = { prompt };
      if (name) body.name = name;
      if (contractType) body.contractType = contractType;
      if (sourceCode) body.sourceCode = sourceCode;
      const response = await axios.post(
        `${blockchainURL}/v2/company/${companyId}/smart-contracts/generate`,
        body,
        { headers: await headers() }
      );
      return response.data;
    },
    
    updateCompanyToken: async (companyId, tokenId, tokenData) => {
      const response = await axios.post(`${blockchainURL}/v1/company/${companyId}/smart-contracts/custom/${tokenId}`, tokenData, { headers: await headers() });
      return response.data;
    },

    getCompanyToken: async (companyId, tokenId) => {
      const response = await axios.get(`${blockchainURL}/v1/company/${companyId}/smart-contracts/custom/${tokenId}`, { headers: await headers() });
      return response.data;
    },

    deleteCompanyToken: async (companyId, tokenId) => {
      const response = await axios.delete(`${blockchainURL}/v1/company/${companyId}/smart-contracts/custom/${tokenId}/`, { headers: await headers() });
      return response.data;
    },

    // Currencies API
    getCurrencies: async () => {
      const response = await axios.get(`${blockchainURL}/v1/currencies`, { headers: await headers() });
      return response.data?.currencies || [];
    },

    // NFT API
    createRwaNft: async (companyId, rwaId, nftData) => {
      const response = await axios.post(`${rwaURL}/v1/company/${companyId}/${rwaId}/nft`, nftData, { headers: await headers() });
      return response.data;
    },

    getRwaNfts: async (companyId, rwaId) => {
      const response = await axios.get(`${rwaURL}/v1/company/${companyId}/${rwaId}/nft`, { headers: await headers() });
      return response.data;
    },

    deleteRwaNft: async (companyId, rwaId, nftId) => {
      const response = await axios.delete(`${rwaURL}/v1/company/${companyId}/${rwaId}/nft/${nftId}`, { headers: await headers() });
      return response.data;
    },

    updateRwaNft: async (companyId, rwaId, nftId, nftData) => {
      const response = await axios.post(`${rwaURL}/v1/company/${companyId}/${rwaId}/nft/${nftId}/data`, nftData, { headers: await headers() });
      return response.data;
    },

    // Integrations API
    getCompanyIntegrations: async (companyId) => {
      const response = await axios.get(`${integrationUrl}/v1/${companyId}`, { headers: await headers() });
      return response.data;
    },

    upsertCompanyIntegration: async (companyId, integrationData) => {
      const response = await axios.post(`${integrationUrl}/v1/${companyId}`, integrationData, { headers: await headers() });
      return response.data;
    },

    deleteCompanyIntegration: async (companyId) => {
      const response = await axios.delete(`${integrationUrl}/v1/${companyId}`, { headers: await headers() });
      return response.data;
    },

    testIntegration: async (companyId, integrationData) => {
      const response = await axios.post(`${integrationUrl}/v1/${companyId}/test`, integrationData, { headers: await headers() });
      return response.data;
    },

    // Abi API
    getAbis: async () => {
      const response = await axios.get(`${blockchainURL}/v1/abi`, { headers: await headers() });
      return response.data?.abis || {};
    },

    // Admin Source Code API (bucket: smart-contracts-source)
    getAdminSourceCodes: async () => {
      const response = await axios.get(`${blockchainURL}/v2/admin/source`, { headers: await headers() });
      return response.data?.sources || response.data?.sourceCodes || response.data || [];
    },

    getAdminSourceCode: async (name) => {
      const response = await axios.get(`${blockchainURL}/v1/admin/source/${encodeURIComponent(name)}`, { headers: await headers() });
      return response.data;
    },

    createAdminSourceCode: async ({ name, content }) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/source`, { name, content }, { headers: await headers() });
      return response.data;
    },

    updateAdminSourceCode: async (name, { content }) => {
      const response = await axios.put(`${blockchainURL}/v1/admin/source/${encodeURIComponent(name)}`, { content }, { headers: await headers() });
      return response.data;
    },

    deleteAdminSourceCode: async (name) => {
      const response = await axios.delete(`${blockchainURL}/v1/admin/source/${encodeURIComponent(name)}`, { headers: await headers() });
      return response.data;
    },

    deployAdminSource: async (name, { chainId, constructorArguments }) => {
      const response = await axios.post(
        `${blockchainURL}/v2/admin/source/${encodeURIComponent(name)}/deploy`,
        { chainId, constructorArguments },
        { headers: await headers() }
      );
      return response.data;
    },

    // Admin ABI API (bucket: ${env}-abis)
    getAdminAbis: async () => {
      const response = await axios.get(`${blockchainURL}/v1/admin/abi`, { headers: await headers() });
      return response.data?.abis || response.data || [];
    },

    getAdminAbi: async (name) => {
      const response = await axios.get(`${blockchainURL}/v1/admin/abi/${encodeURIComponent(name)}`, { headers: await headers() });
      return response.data;
    },

    createAdminAbi: async ({ name, abi }) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/abi`, { name, abi }, { headers: await headers() });
      return response.data;
    },

    updateAdminAbi: async (name, { abi }) => {
      const response = await axios.put(`${blockchainURL}/v1/admin/abi/${encodeURIComponent(name)}`, { abi }, { headers: await headers() });
      return response.data;
    },

    deleteAdminAbi: async (name) => {
      const response = await axios.delete(`${blockchainURL}/v1/admin/abi/${encodeURIComponent(name)}`, { headers: await headers() });
      return response.data;
    },

    generateAdminAbiFromSource: async (sourceCodeName) => {
      const response = await axios.get(`${blockchainURL}/v1/admin/source/${encodeURIComponent(sourceCodeName)}/abi`, { headers: await headers() });
      return response.data;
    },

    // Overview API
    getCompanyOverview: async (companyId) => {
      const response = await axios.get(`${rwaURL}/v1/company/${companyId}/overview`, { headers: await headers() });
      return response.data;
    },


    // Ramp API
    getRampOperators: async () => {
      const response = await axios.get(`${blockchainURL}/v1/ramp/operators`, { headers: await headers() });
      return response.data?.operators || [];
    },

    getAdminRampMethods: async () => {
      const response = await axios.get(`${blockchainURL}/v1/admin/ramp/method`, { headers: await headers() });
      return response.data?.methods || [];
    },

    deleteAdminRampMethod: async (methodId) => {
      const response = await axios.delete(`${blockchainURL}/v1/admin/ramp/method/${methodId}`, { headers: await headers() });
      return response.data;
    },

    adminCreateRampMethod: async (methodData) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/ramp/method`, methodData, { headers: await headers() });
      return response.data;
    },

    adminUpdateRampMethod: async (methodId, methodData) => {
      const response = await axios.post(`${blockchainURL}/v1/admin/ramp/method/${methodId}`, methodData, { headers: await headers() });
      return response.data;
    },

    getPaybisPairs: async () => {
      const response = await axios.get(`${blockchainURL}/v2/wallet/ramp/operators/paybis/pairs`, { headers: await headers() });
      return response.data?.data || [];
    },

    // Ramp Sessions API
    getAdminRampSessions: async (page = 1, limit = 10) => {
      const response = await axios.get(`${blockchainURL}/v1/admin/ramp/session/list?page=${page}&limit=${limit}`,{ headers: await headers() });
      return response.data;
    },

    // Workflows API
    listWorkflows: async (companyId) => {
      const response = await axios.get(`${workflowUrl}/v1/${companyId}/`, { headers: await headers() });
      return response.data;
    },

    createWorkflow: async (companyId, { name, description }) => {
      const response = await axios.post(`${workflowUrl}/v1/${companyId}/`, { name, description }, { headers: await headers() });
      return response.data;
    },

    getWorkflow: async (companyId, workflowId, versionId) => {
      const params = versionId ? { versionId } : {};
      const response = await axios.get(`${workflowUrl}/v1/${companyId}/${workflowId}/`, { headers: await headers(), params });
      return response.data;
    },

    updateWorkflow: async (companyId, workflowId, data) => {
      const response = await axios.post(`${workflowUrl}/v1/${companyId}/${workflowId}/`, data, { headers: await headers() });
      return response.data;
    },

    uploadWorkflowFile: async (companyId, workflowId, fileContent) => {
      const response = await axios.put(`${workflowUrl}/v1/${companyId}/${workflowId}/`, fileContent, { headers: await headers() });
      return response.data;
    },

    deleteWorkflow: async (companyId, workflowId) => {
      const response = await axios.delete(`${workflowUrl}/v1/${companyId}/${workflowId}/`, { headers: await headers() });
      return response.data;
    },

    runWorkflow: async (companyId, workflowId, inputData = {}) => {
      const response = await axios.post(`${workflowUrl}/v1/${companyId}/${workflowId}/run`, inputData, { headers: await headers() });
      return response.data;
    },

    generateWorkflow: async (companyId, workflowId, prompt) => {
      const response = await axios.post(`${workflowUrl}/v1/${companyId}/${workflowId}/generate`, { prompt }, { headers: await headers() });
      return response.data;
    },

    getWorkflowExecutions: async (companyId, workflowId) => {
      const response = await axios.get(`${workflowUrl}/v1/${companyId}/${workflowId}/executions`, { headers: await headers() });
      return response.data;
    },

    modifyWorkflow: async (companyId, workflowId, prompt, workflow) => {
      const response = await axios.post(`${workflowUrl}/v1/${companyId}/${workflowId}/modify`, { prompt, workflow }, { headers: await headers() });
      return response.data;
    },
  }
}